import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { AuthLayout, PoweredBy } from "../components/AuthLayout";
import { useNative } from "../../shared/phone/media";
import { SignupNative } from "./AuthNative";

/**
 * Invited partner signup. An invited partner sets a password on the email they
 * were invited with; a partner arriving through a join link (/join/:slug)
 * self-applies to that product's program. Real account creation only, tied to a
 * real invite or a real join link.
 */
export function SignupView() {
  const { slug } = useParams<{ slug: string }>();
  const { signup } = usePartnerAuth();
  const navigate = useNavigate();

  const [productName, setProductName] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const native = useNative();

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    partnerApi
      .joinInfo(slug)
      .then((info) => {
        if (alive && info.exists) setProductName(info.productName);
      })
      .catch(() => {
        /* an unknown slug just shows the generic invite copy */
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signup({
        firstName,
        lastName,
        email,
        phone: phone || null,
        password,
        productSlug: slug ?? null,
      });
      navigate("/programs", { replace: true });
    } catch (err) {
      setError(
        err instanceof PartnerApiError ? err.message : "Could not create your account. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (native) {
    return (
      <SignupNative
        productName={productName}
        firstName={firstName}
        lastName={lastName}
        email={email}
        phone={phone}
        password={password}
        onFirst={setFirstName}
        onLast={setLastName}
        onEmail={setEmail}
        onPhone={setPhone}
        onPassword={setPassword}
        error={error}
        busy={busy}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <AuthLayout>
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="eyebrow">You are invited</div>
        <h1>Create your account</h1>
        <p className="sub">
          {productName ? `Join the ${productName} partner program.` : "Join the partner program."}
        </p>

        {error && <div className="auth-error" role="alert">{error}</div>}

        <div className="frow">
          <div className="field">
            <label htmlFor="su-first">First name</label>
            <input
              id="su-first"
              className="inp"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="su-last">Last name</label>
            <input
              id="su-last"
              className="inp"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="su-email">Email</label>
          <input
            id="su-email"
            className="inp"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="su-phone">Phone</label>
          <input
            id="su-phone"
            className="inp"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="su-pass">Password</label>
          <input
            id="su-pass"
            className="inp"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </div>

        <button className="btn wide" type="submit" disabled={busy}>
          {busy ? "Creating account..." : "Create account"}
        </button>
        <div className="auth-alt">
          Already a partner? <Link to="/login">Log in</Link>
        </div>
        <PoweredBy />
      </form>
    </AuthLayout>
  );
}
